export type InsightCategory =
  | "Business"
  | "Focus"
  | "Leadership"
  | "Discipline"
  | "Confidence"
  | "Growth"
  | "Perspective";

export interface Insight {
  id: string;
  text: string;
  category: InsightCategory;
}

export const INSIGHT_CATEGORIES: InsightCategory[] = [
  "Business",
  "Focus",
  "Leadership",
  "Discipline",
  "Confidence",
  "Growth",
  "Perspective",
];

export const INSIGHTS: Insight[] = [
  // Business
  { id: "b1", category: "Business", text: "Revenue follows trust. Build trust first, the numbers follow." },
  { id: "b2", category: "Business", text: "Profit is a byproduct of solving a real problem better than anyone else." },
  { id: "b3", category: "Business", text: "Your business reflects your standards, not your ambitions." },
  { id: "b4", category: "Business", text: "The market rewards consistency over brilliance." },
  { id: "b5", category: "Business", text: "Stop planning. Start testing. Reality teaches faster than theory." },
  { id: "b6", category: "Business", text: "Cash flow is oxygen. Protect it before you chase growth." },

  // Focus
  { id: "f1", category: "Focus", text: "What you focus on grows. What you ignore decays." },
  { id: "f2", category: "Focus", text: "Your biggest competitor is your own distraction." },
  { id: "f3", category: "Focus", text: "Clarity creates momentum. Confusion creates exhaustion." },
  { id: "f4", category: "Focus", text: "Not everything needs expansion. Some things need elimination." },
  { id: "f5", category: "Focus", text: "The real bottleneck is rarely capacity. It is attention." },
  { id: "f6", category: "Focus", text: "If everything is a priority, nothing is." },

  // Leadership
  { id: "l1", category: "Leadership", text: "Your team mirrors your energy. Lead the room you walk into." },
  { id: "l2", category: "Leadership", text: "Delegate the task, never the standard." },
  { id: "l3", category: "Leadership", text: "Leadership is the willingness to be misunderstood for a while." },
  { id: "l4", category: "Leadership", text: "A leader removes friction so others can do their best work." },
  { id: "l5", category: "Leadership", text: "Hire for character, train for skill, fire for culture." },
  { id: "l6", category: "Leadership", text: "The hardest decision today is the easiest one to live with tomorrow." },

  // Discipline
  { id: "d1", category: "Discipline", text: "Discipline is choosing what you want most over what you want now." },
  { id: "d2", category: "Discipline", text: "Motivation gets you started. Systems keep you going." },
  { id: "d3", category: "Discipline", text: "Small actions, repeated daily, beat heroic effort every time." },
  { id: "d4", category: "Discipline", text: "The decision you keep avoiding is the one moving everything." },
  { id: "d5", category: "Discipline", text: "Show up when it is boring. That is where compounding lives." },
  { id: "d6", category: "Discipline", text: "Standards rise to the lowest behavior you tolerate." },

  // Confidence
  { id: "c1", category: "Confidence", text: "Confidence is built by keeping promises to yourself." },
  { id: "c2", category: "Confidence", text: "You do not need to feel ready. You need to begin." },
  { id: "c3", category: "Confidence", text: "Own the room by being the most prepared person in it." },
  { id: "c4", category: "Confidence", text: "Speak your price without flinching. The flinch costs more than the number." },
  { id: "c5", category: "Confidence", text: "Doubt is normal. Let it inform you, not run you." },

  // Growth
  { id: "g1", category: "Growth", text: "What got you here will not get you there." },
  { id: "g2", category: "Growth", text: "Growth demands discomfort. Lean in." },
  { id: "g3", category: "Growth", text: "Your next level requires a different focus, not more effort at this one." },
  { id: "g4", category: "Growth", text: "Reinvest in the engine before you celebrate the engine." },
  { id: "g5", category: "Growth", text: "The fastest growth comes from removing what no longer fits." },
  { id: "g6", category: "Growth", text: "Hire the person you needed twelve months ago." },

  // Perspective
  { id: "p1", category: "Perspective", text: "Zoom out. Most emergencies are not what they feel like." },
  { id: "p2", category: "Perspective", text: "The problem you have today is a milestone someone else is praying for." },
  { id: "p3", category: "Perspective", text: "Your customers already know the truth about your business. Listen." },
  { id: "p4", category: "Perspective", text: "Speed is often better than certainty." },
  { id: "p5", category: "Perspective", text: "The answer is usually in the simplicity you are resisting." },
  { id: "p6", category: "Perspective", text: "Time spent thinking is rarely time wasted." },
];

export function getRandomInsight(pool: Insight[] = INSIGHTS, exclude?: string): Insight {
  const candidates = exclude ? pool.filter((i) => i.id !== exclude) : pool;
  const list = candidates.length ? candidates : pool;
  return list[Math.floor(Math.random() * list.length)];
}
